'use client';
import { useTimePilotT } from "@/i18n/useTimePilotT";

import { useMemo } from 'react';
import type {
  MobileFrontEndMapView,
  MobileUiConfigArtifact,
} from '@/shared/mobile-service/api/cors-mobile-api';
import { mapDetailFieldNames, mapPointIdField, mapPointMediaView } from '@/shared/mobile-service/lib/build-map-query';
import { citationMapWindowLabel } from '@/shared/mobile-service/lib/citation-focus';
import { artifactDisplayName } from '@/shared/mobile-service/lib/labels';
import { mapPointMatchesFocus } from '@/shared/mobile-service/lib/map-bounds';
import { mapRowsToPoints } from '@/shared/mobile-service/lib/map-points';
import { ArtifactMap } from './ArtifactMap';

/**
 * Citation-pane map: hydrate's time-range window as points, not a new viewer tab.
 *
 * Reuses {@link ArtifactMap}. Unlike {@link MapView} this has no GraphQL of its
 * own — hydrate already returned the windowed rows and the registry view says
 * how to read coordinates.
 */
export function CitedMap({
  rows,
  view,
  artifact,
  focusIds,
  highlightIds = [],
  viewerHref,
  label,
}: {
  rows: Record<string, unknown>[];
  view: MobileFrontEndMapView;
  artifact?: MobileUiConfigArtifact;
  focusIds: Array<string | number>;
  highlightIds?: Array<string | number>;
  viewerHref: string | null;
  label?: string | null;
}) {
  const t = useTimePilotT();
  const points = useMemo(
    () => mapRowsToPoints(rows, view, mapPointIdField(artifact), 0, artifact?.locations),
    [rows, view, artifact],
  );
  const detailFields = useMemo(() => mapDetailFieldNames(view, artifact), [view, artifact]);
  const mediaPreview = useMemo(() => mapPointMediaView(view, artifact), [view, artifact]);
  const cited = points.filter((point) => mapPointMatchesFocus(point.id, focusIds)).length;
  const windowLabel = citationMapWindowLabel(cited, points.length);
  const sourceLabel = label?.trim() || null;
  const title = sourceLabel || windowLabel || artifactDisplayName(artifact, view.title);
  const subtitle = sourceLabel ? windowLabel : null;

  if (points.length === 0) return null;

  return (
    <div
      data-testid="CitationCitedMap"
      className="flex min-h-0 w-full flex-1 flex-col overflow-hidden rounded-md bg-white/5"
    >
      <header className="flex shrink-0 items-center justify-between gap-3 border-b border-amber-400/40 bg-amber-400/10 px-3 py-2">
        <div className="min-w-0">
          <p className="m-0 truncate text-sm text-amber-100">{title}</p>
          {subtitle ? <p className="m-0 text-xs text-amber-100/70">{subtitle}</p> : null}
        </div>
        {viewerHref ? (
          <a
            href={viewerHref}
            target="_blank"
            rel="noopener noreferrer"
            className="shrink-0 text-xs text-white/80 underline decoration-white/40 underline-offset-2 hover:text-white"
          >{t("viewers.openInMobileViewer")}</a>
        ) : null}
      </header>
      <div className="min-h-0 flex-1 p-2">
        <ArtifactMap
          points={points}
          focusIds={focusIds}
          highlightIds={highlightIds}
          detailFields={detailFields}
          mediaPreview={mediaPreview}
          heightClassName="h-full min-h-0"
        />
      </div>
    </div>
  );
}
